import VisuallyHidden from "@reach/visually-hidden";
import { Box, Flex } from "grid-styled";
import { Component, Fragment, createRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import { themeGet } from "styled-system";
import system from "system-components";


import { isRequiredApproval } from "../approve_policy/utils";

import {
  ApplicabilityTenantsTootip,
  AuthorSearchLink,
  CategorySearchLink,
  PreviewValueSpan
} from "./PolicyProperties";
import { ApprovalStatusLink, showEffectiveBadge } from "./StatusBar";

import { parseDocumentStatusToTranslationKey } from "pstat-anywhere/utils/document";
import { Text } from "pstat-design-system/text";
import Modal from "pstat-design-system/modal";
import {
  Badge,
  LinkedBadge,
  RouterLinkedBadge
} from "pstat-design-system/Badge";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import { withTenantContext } from "pstat-anywhere/context_providers/TenantContext";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import { StyledFontAwesomeIcon } from "pstat-anywhere/components/partials/icons";
import {RegulationItem} from "pstat-anywhere/components/document_control/view_policy/standards_and_regulations/StandardsAndRegulationsPanel";

const Item = system({
  is: Box,
  mr: 4,
  mb: 4
});

const ModalPolicyStatus = ({ status }) => {
  const { t } = useTranslation();
  const formattedStatus = t(parseDocumentStatusToTranslationKey(status))
  return (
    <Item>
      <VisuallyHidden>
        {t("documentControl.view.infoModal.modalPolicyStatus.visuallyHidden", {
          formattedStatus
        })}
      </VisuallyHidden>
      <Text display="block" aria-hidden="true">
        {t("documentControl.view.infoModal.modalPolicyStatus.title")}
      </Text>
      <Badge mx={0} aria-hidden="true">
        {formattedStatus}
      </Badge>
    </Item>
  );
};
const PkDisplay = ({ pk }) => {
  const { t } = useTranslation();
  return (
    <Item>
      <VisuallyHidden>
        {t("documentControl.view.infoModal.pkDisplay.visuallyHidden", { pk })}
      </VisuallyHidden>
      <Text display="block" aria-hidden="true">
        {t("documentControl.view.infoModal.pkDisplay.title")}
      </Text>
      <Badge mx={0} aria-hidden="true">
        {pk}
      </Badge>
    </Item>
  );
};

const ApprovalStatus = ({ approvalSteps, workflowTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  function handleModalToggle() {
    setIsOpen(!isOpen);
  }
  return (
    <Item onClick={handleModalToggle}>
      <ApprovalStatusLink
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        workflowTitle={workflowTitle}
        approvalSteps={approvalSteps}
      />
    </Item>
  );
};

export const EffectiveDate = ({ effectiveDate }) => (
  <Item>
    <Text display="block">Effective</Text>
    <LinkedBadge display="inline-block" height="inherit" mx={0}>
      {effectiveDate}
    </LinkedBadge>
  </Item>
);

const SectionDivider = system({
  borderTop: 1,
  borderColor: "nav.80",
  mb: 4
});

const ModalContent = system({
  p: 4
});

const DateDisplay = ({ label, value }) => {
  const { t } = useTranslation();
  let displayValue = value;
  let screenReaderFullDate = value;
  if (value === null) {
    displayValue = "-";
    screenReaderFullDate = t(
      "documentControl.view.infoModal.dateDisplay.notApplicable"
    );
  }
  return (
    <Item>
      <VisuallyHidden>{`${label}, ${screenReaderFullDate}`}</VisuallyHidden>
      <Text display="block" color="nav.25" aria-hidden="true">
        {label}
      </Text>
      <Text aria-hidden="true">{displayValue}</Text>
    </Item>
  );
};

const KeyValuePair = props => (
  <Item>
    <Text ml={1} color="nav.25">
      {props.label}
    </Text>
    {props.children}
  </Item>
);

const Focuser = system({
  display: "inline-block",
  tabIndex: "0"
}).extend`
  &:focus {
    outline: 0;
  }
`;

const FocusBorder = system({
  border: 1,
  borderColor: "transparent",
  borderRadius: 1,
  display: "inline-block",
  p: 1,
  tabIndex: "-1"
}).extend`
  &:focus {
    outline: 0;
  }

  ${Focuser}:focus & {
    border: dashed 1px;
    border-color: ${themeGet("colors.nav.25")}
  }
`;

class Applicability extends Component {
  constructor(props) {
    super(props);

    this.applicabilityRef = createRef();
    this.tooltipTimeout = null;
  }

  componentWillUnmount() {
    clearTimeout(this.tooltipTimeout);
  }

  handleKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      ReactTooltip.show(this.applicabilityRef.current);
      this.tooltipTimeout = setTimeout(() => {
        ReactTooltip.hide(this.applicabilityRef.current);
      }, 2000);
    }
  };

  render() {
    const { document } = this.props;
    return (
      <Focuser
        data-tip={true}
        data-for="applicability-tenants-tooltip"
        data-event="mousedown"
        data-event-off="mouseup"
        data-delay-hide="2000"
        innerRef={this.applicabilityRef}
        onKeyDown={this.handleKeyDown}
      >
        <FocusBorder>
          <StyledFontAwesomeIcon
            icon={["fal", "question-circle"]}
            color="secondary.0"
          />
          <Text color="secondary.0">{document.bloc.name}</Text>
          <ApplicabilityTenantsTootip bloc={document.bloc} />
        </FocusBorder>
      </Focuser>
    );
  }
}

const References = props => (
  <div>
    {props.tags.map((tag, index) => {
      const to = {
        pathname: `${getRootUrl()}/search`,
        query: {
          reference: tag.pk
        }
      };
      if (props.isPreview) {
        let tagText = tag.name;
        if (index !== props.tags.length - 1) {
          tagText += ",";
        }
        return <PreviewValueSpan>{tagText}</PreviewValueSpan>;
      } else {
        return (
          <RouterLinkedBadge display="inline-block" to={to}>
            {tag.name}
          </RouterLinkedBadge>
        );
      }
    })}
  </div>
);

const Regulations = ({ regulations }) => {
  return (
    regulations.map((regulation, index) => {
      return (
        <RegulationItem
          {...regulation}
          isLast={index === (regulations.length - 1)}
        />
      )
    })
  );
};

const ViewDocumentInfoModalContent = ({
  document,
  labels,
  tenant,
  isPreview,
  t
}) => (
  <Fragment>
    <Flex wrap="wrap">
      <Flex wrap="wrap">
        <PkDisplay {...document} />
        <ModalPolicyStatus {...document} />
      </Flex>
      <Flex wrap="wrap">
        {isRequiredApproval(document) && (
          <ApprovalStatus
            workflowTitle={
              document.approvalWorkflow && document.approvalWorkflow.name
            }
            approvalSteps={
              document.approvalWorkflow && document.approvalWorkflow.steps
            }
          />
        )}
        {showEffectiveBadge(tenant, document) && (
          <EffectiveDate {...document} />
        )}
      </Flex>
    </Flex>
    <SectionDivider />
    <Flex wrap="wrap">
      {tenant.documentSettings.originationDateShow && (
        <DateDisplay
          label={labels.originationDateLabel}
          value={
            tenant.hasScheduledEffectiveDateEnabled === true
              ? document.originationDate
              : document.legacyEffectiveDate
          }
        />
      )}
      {tenant.documentSettings.approvedDateShow && (
        <DateDisplay
          label={labels.approvedDateLabel}
          value={document.approvalDate}
        />
      )}
      {tenant.hasScheduledEffectiveDateEnabled &&
        tenant.documentSettings.effectiveDateShow && (
          <DateDisplay
            label={labels.effectiveDateLabel}
            value={document.effectiveDate}
          />
        )}
      {tenant.documentSettings.revisionDateShow && (
        <DateDisplay
          label={labels.revisionDateLabel}
          value={document.revisionDate}
        />
      )}
      {tenant.documentSettings.expirationDateShow && (
        <DateDisplay
          label={labels.expirationDateLabel}
          value={document.expirationDate}
        />
      )}
    </Flex>
    <SectionDivider />
    {tenant.documentSettings.authorShow && (
      <KeyValuePair label={labels.authorLabel}>
        <div>
          {isPreview ? (
            <PreviewValueSpan>
              {document.author.firstName} {document.author.lastName}
            </PreviewValueSpan>
          ) : (
            <AuthorSearchLink author={document.author} />
          )}
        </div>
      </KeyValuePair>
    )}
    {tenant.documentSettings.departmentShow && (
      <KeyValuePair label={labels.categoryLabel}>
        <div>
          {isPreview ? (
            <PreviewValueSpan>{document.category.name}</PreviewValueSpan>
          ) : (
            <CategorySearchLink category={document.category} />
          )}
        </div>
      </KeyValuePair>
    )}
    <KeyValuePair label={t("tables.labels.applicability")}>
      <div>
        <Applicability document={document} />
      </div>
    </KeyValuePair>
    {tenant.documentSettings.referencesShow && (
      <Fragment>
        {document.tags.length > 0 && (
          <KeyValuePair label={labels.referencesLabel}>
            <References tags={document.tags} isPreview={isPreview} />
          </KeyValuePair>
        )}
      </Fragment>
    )}
    {document.regulations && document.regulations.length > 0 && (
      <Fragment>
        <KeyValuePair
          id="standards-and-regulations"
          label={t("nav.primary.manage.standardsAndRegs")}
        >
          <Flex wrap="wrap">
            <Regulations regulations={document.regulations} />
          </Flex>
        </KeyValuePair>
      </Fragment>
    )}
  </Fragment>
);

const EditDocumentInfoModalContent = ({ document }) => (
  <Flex wrap="wrap">
    <Flex wrap="wrap">
      <PkDisplay {...document} />
      <ModalPolicyStatus {...document} />
    </Flex>
  </Flex>
);

class InfoModal extends Component {
  render() {
    const {
      document,
      labels,
      tenant,
      t,
      isOpen,
      onClose,
      isPreview,
      isViewDocument
    } = this.props;
    const { documentLabel } = labels;
    return (
      <Modal
        contentLabel={t("documentControl.view.infoModal.contentLabel", {documentLabel})}
        isOpen={isOpen}
        onRequestClose={onClose}
      >
        <ModalContent>
          {isViewDocument ? (
            <ViewDocumentInfoModalContent
              document={document}
              labels={labels}
              tenant={tenant}
              isPreview={isPreview}
              t={t}
            />
          ) : (
            <EditDocumentInfoModalContent document={document} />
          )}
        </ModalContent>
      </Modal>
    );
  }
}

export default withTenantContext(
  withLabelContext(withTranslation()(InfoModal))
);
